import { Button } from "@mui/material";
import { controlBandButtonSx } from "../../theme/compact.js";
import type { ReactElement } from "react";
// Re-use the canonical `Density` shape from the legacy DataGridToolbar
// primitive — single source of truth for the type.
import type { Density } from "../DataGridToolbar/DataGridToolbar.js";
import { LayoutGridIcon, Rows3Icon } from "@aeriosiq/ui/icons";

export type { Density };

/**
 * `<DensityToggleButton>` — text-button toggle for grid row density.
 *
 * Spec: `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 6 /
 * DensityToggleButton".
 *
 * - Visible text: `"Density (compact)"` / `"Density (standard)"` so the
 *   toggle's purpose AND its current value are exposed to AT (avoids
 *   the aria-label-overrides-visible-text trap).
 * - Icon swap: `<Rows3Icon>` for compact (denser rows), `<LayoutGridIcon>`
 *   for standard (looser rows). Counter-intuitive at first glance — but
 *   matches the icon-as-affordance convention: "what would tapping this
 *   give me?" not "what does this currently show?".
 * - `aria-pressed={density === "compact"}` so the toggle state is
 *   exposed to AT.
 */
export interface DensityToggleButtonProps {
  density: Density;
  onChange: (next: Density) => void;
}

export function DensityToggleButton({
  density,
  onChange,
}: DensityToggleButtonProps): ReactElement {
  const nextDensity: Density = density === "compact" ? "standard" : "compact";
  const stateSuffix = density === "compact" ? " (compact)" : " (standard)";
  const Icon = density === "compact" ? Rows3Icon : LayoutGridIcon;
  return (
    <Button
      size="small"
      variant="text"
      color="inherit"
      // The dense band (D77 compact, 32px) — see `denseButtonSx`.
      sx={controlBandButtonSx}
      startIcon={<Icon fontSize="small" />}
      onClick={() => {
        onChange(nextDensity);
      }}
      aria-pressed={density === "compact"}
      title={`Switch to ${nextDensity} rows`}
    >
      Density{stateSuffix}
    </Button>
  );
}
